import { useState } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';
import { UserContextProject } from 'core/userContext';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { InfoBanner } from 'ui/banner';
import { BasicFilterSelect } from 'ui/selects';
import { body2, spacing } from 'ui/theme';
import { TOGGLE_SIZES, Toggle } from 'ui/toggle';
import { pxToRem } from 'utils/convert';
import noop from 'utils/noop';
import { getOriginalNodes } from '../../../ducks';
import { ProjectType } from '../../../types';
import { CloningCampaigns } from './CloningCampaigns';
import { CloneCampaignsPopover } from './CloneCampaignsPopover';
import { Campaign } from './context/CloningCampaignsContext.types';
import { draftMessagingPermissionsProjectsQuery } from './cloning.graphql';
import { getWorkflowContentToCopy } from './utils';
import {
  CAMPAIGN_TOGGLE,
  CLONE_JOURNEY_PROJECT_SELECT,
  CLONE_TO_DIFFERENT_PROJECT_MESSAGE,
  DIFF_PROJECT_INFO_BANNER,
} from './constants';

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;

  > * {
    margin-left: ${spacing.half};
  }
`;

const ToggleLabel = styled.label`
  margin-bottom: 0;
  font-weight: normal;
  ${body2}
`;

const StyledBasicFilterSelect = styled(BasicFilterSelect)<{ $isCurrentProject: boolean }>`
  > div {
    > div {
      max-height: ${pxToRem(205)};
      > button {
        > div {
          display: flex;
          justify-content: space-between;
        }
        /** only shows "current project" in the trigger */
        ${({ $isCurrentProject, theme }) =>
          $isCurrentProject &&
          `
        > div::after {
          float: right;
          content: 'Current project';
          color: ${theme?.colors?.textColors?.textSecondary};
        }`}
      }
    }
  }
`;

type CloneJourneyProjectSelectProps = {
  setSelectedProject: (id: number) => void;
  currentProject?: UserContextProject;
  selectedProject?: number;
  setCopyCampaigns?: (flag: boolean) => void;
  copyCampaigns?: boolean;
  setCopyWebhooks?: (flag: boolean) => void;
  copyWebhooks?: boolean;
  campaignNodes?: Campaign[];
};

// local "feature flag", product doesn't want webhook copying at the moment
export const ALLOW_WEBHOOKS = false;

export const CloneJourneyProjectSelect = ({
  setSelectedProject,
  currentProject,
  selectedProject,
  setCopyCampaigns = noop,
  copyCampaigns = false,
  setCopyWebhooks = noop,
  copyWebhooks = false,
  campaignNodes = [],
}: CloneJourneyProjectSelectProps) => {
  /** The  clone will be a copy of the last saved version so we want to make sure we are showing the toggles based on the saved nodes */
  const nodes = useSelector(getOriginalNodes);
  const [projects, setProjects] = useState<ProjectType[]>([]);

  const { isLoading: isProjectsLoading } = useGraphql(draftMessagingPermissionsProjectsQuery, {
    onCompleted: (data) => setProjects(data.accessibleProjects || []),
  });

  const options = projects?.map((project) => {
    const currentProjectSuffix = currentProject?.id === project?.id ? 'Current project' : '';
    return {
      id: `${project?.id}`,
      value: project?.id,
      label: project?.name,
      Suffix: currentProjectSuffix,
    };
  });

  const { hasWebhooks, hasCampaigns, hasContentToCopy } = getWorkflowContentToCopy(
    nodes,
    campaignNodes,
  );
  const isDifferentProject = selectedProject !== currentProject?.id;
  const checkCampaignTemplates = copyCampaigns && hasCampaigns && isDifferentProject;

  return (
    <>
      <span>Project</span>
      <StyledBasicFilterSelect
        data-test={CLONE_JOURNEY_PROJECT_SELECT}
        onSelect={([val]) => setSelectedProject(+val)}
        defaultValues={currentProject && projects.length ? [currentProject?.id] : []}
        placeholder={isProjectsLoading ? 'Loading...' : ''}
        id="project-dropdown"
        inputContent={{ filterPlaceholder: 'Search options' }}
        isLoading={isProjectsLoading}
        options={options}
        autoFocusOnInput
        shouldFocusOnFirstElementByDefault={false}
        $isCurrentProject={!isDifferentProject}
        isFullWidth
      />
      {isDifferentProject && (
        <>
          <InfoBanner data-test={DIFF_PROJECT_INFO_BANNER}>
            {CLONE_TO_DIFFERENT_PROJECT_MESSAGE}
          </InfoBanner>

          {hasContentToCopy && <span>Include with copy</span>}

          {hasCampaigns && (
            <ToggleContainer data-test={CAMPAIGN_TOGGLE}>
              <Toggle
                id="campaign-toggle"
                labelId="labelId"
                onClick={() => setCopyCampaigns(!copyCampaigns)}
                size={TOGGLE_SIZES.SMALL}
                isActive={copyCampaigns}
              />
              <ToggleLabel>Campaign content</ToggleLabel>
              <CloneCampaignsPopover />
            </ToggleContainer>
          )}

          {ALLOW_WEBHOOKS && hasWebhooks && (
            <ToggleContainer data-test="webhook-toggle">
              <Toggle
                id="webhook-toggle"
                labelId="labelId"
                onClick={() => setCopyWebhooks(!copyWebhooks)}
                size={TOGGLE_SIZES.SMALL}
                isActive={copyWebhooks}
              />
              <ToggleLabel>Webhooks</ToggleLabel>
            </ToggleContainer>
          )}
        </>
      )}

      {checkCampaignTemplates && <CloningCampaigns />}
    </>
  );
};
